import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Logo } from './ui/Logo';

interface AppLayoutProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'ダッシュボード' },
  { to: '/streaming', label: '配信' },
  { to: '/sessions', label: '履歴' },
  { to: '/streaming-profiles', label: '配信先' },
  { to: '/settings', label: '設定' },
];

export function AppLayout({ title, description, actions, children }: AppLayoutProps) {
  const { performer, logout } = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 24px',
          borderBottom: 'var(--border-default)',
          background: 'var(--color-frame)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '28px' }}>
          <Logo size="sm" />
          <nav style={{ display: 'flex', gap: '4px' }}>
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) => ({
                  fontSize: '12px',
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-md)',
                  color: isActive
                    ? 'var(--color-text-primary)'
                    : 'var(--color-text-secondary)',
                  background: isActive ? 'rgba(255,255,255,0.04)' : 'transparent',
                  textDecoration: 'none',
                })}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
            {performer?.display_name}
          </span>
          <span
            style={{
              fontSize: '9px',
              fontWeight: 500,
              color: performer?.plan === 'pro' ? 'white' : 'var(--color-text-secondary)',
              background:
                performer?.plan === 'pro'
                  ? 'var(--gradient-cta)'
                  : 'rgba(255,255,255,0.04)',
              border: performer?.plan === 'pro' ? 'none' : 'var(--border-default)',
              padding: '2px 6px',
              borderRadius: '3px',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            {performer?.plan ?? '—'}
          </span>
          <button
            onClick={handleLogout}
            style={{
              background: 'transparent',
              border: 'var(--border-strong)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-secondary)',
              fontSize: '11px',
              padding: '5px 10px',
              cursor: 'pointer',
            }}
          >
            ログアウト
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 24px 48px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: 500, margin: '0 0 4px' }}>
              {title}
            </h1>
            {description && (
              <p
                style={{
                  margin: 0,
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {description}
              </p>
            )}
          </div>
          {actions && <div style={{ flexShrink: 0 }}>{actions}</div>}
        </div>
        {children}
      </main>
    </div>
  );
}
